import React, { useEffect, useState } from "react";
import Select from 'react-select';
import { useGetCompanies, useGetCompanyLocations } from "../../backend/masters";
import { DEFAULT_OPTIONS_PAYLOAD } from "./Table";
import { getValue } from "../../utils/common";
import { include } from "underscore";
const DEFAULT_VALUE = 'ALL';
const DEFAULT_LABEL = 'All';
const DEFAULT_OPTION = { value: DEFAULT_VALUE, label: DEFAULT_LABEL };

const INCLUDE_COMPANY_ASSOCIATIONS = ['locations','parent_company'];
const INCLUDE_COMPANY_LOCATION_ASSOCIATIONS = {
  company: {}, location: { city: 'state' }
}

function OptionalLocations({ onChange }: any) {
    const [associateCompanies, setAssociateCompanies] = useState<any>(null);
    const [allLocations, setAllLocations] = useState<any[]>([]);
    const [locations, setLocations] = useState<any>(null);
    const [states, setStates] = useState<any[]>([]);
    const [company, setCompany] = useState<any>(DEFAULT_OPTION);
    const [associate_company, setAssociateCompany] = useState<any>(DEFAULT_OPTION);
    const [selectedState, setSelectedState] = useState<any>(DEFAULT_OPTION);
    const [location, setLocation] = useState<any>(DEFAULT_OPTION);
    const { companies, isFetching } = useGetCompanies({
        ...DEFAULT_OPTIONS_PAYLOAD, filters: { is_parent_eq: true, is_active_eq: true }
    });
    const { companies: acs, isFetching: fetchingAcs } = useGetCompanies({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: {
          is_parent_eq: false,
          parent_company_id_eq: (company || {}).value,
          is_active_eq: true
        },
        includes: INCLUDE_COMPANY_ASSOCIATIONS
    }, company && company.value && company.value !== DEFAULT_VALUE);

    const { locations: locs, isFetching: fetchingLocs } = useGetCompanyLocations({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: { company_id_eq: (associate_company || {}).value },
        sort: ['location_name asc'],
        include: { location: ['state'] },
        includes: { location: ['state'] }
    }, Boolean(associate_company && associate_company.value && associate_company.value !== DEFAULT_VALUE));

    function handleCompanyChange(event: any) {
        const { value } = event;
        setLocations(null);
        setAssociateCompanies(null);
        setStates([]);
        setAllLocations([]);
        setCompany(event);
        setAssociateCompany(DEFAULT_OPTION);
        setSelectedState(DEFAULT_OPTION);
        setLocation(DEFAULT_OPTION);
        onChange(value === DEFAULT_VALUE ? {} : { company_id_eq: value });
    }

    function handleAssociateCompanyChange(event: any) {
        const { value } = event;
        setAssociateCompany(event);
        setSelectedState(DEFAULT_OPTION);
        setLocation(DEFAULT_OPTION);
        setLocations(null);
        setStates([]);
        setAllLocations([]);
        if (value === DEFAULT_VALUE) {
            onChange({ company_id_eq: company.value });
        } else {
            onChange({ company_id_eq: company.value, associate_company_id_eq: value });
        }
    }

    function handleStateChange(event: any) {
        const { value } = event;
        const isSelectAll = value === DEFAULT_VALUE;
        const filtered = isSelectAll
            ? allLocations
            : allLocations.filter((loc: any) => loc.state_id === value);
        setLocations(filtered);
        setSelectedState(event);
        setLocation(DEFAULT_OPTION);
        const baseFilters: any = { company_id_eq: company.value, associate_company_id_eq: associate_company.value };
        if (!isSelectAll && filtered.length > 0) {
            baseFilters.location_id_in = filtered.map((loc: any) => loc.value);
        }
        onChange(baseFilters);
    }

    function handleLocationChange(event: any) {
        const value = event ? event.value : DEFAULT_VALUE;
        setLocation(event || DEFAULT_OPTION);
        const baseFilters: any = { company_id_eq: company.value, associate_company_id_eq: associate_company.value };
        if (value === DEFAULT_VALUE || !event) {
            if (selectedState && selectedState.value !== DEFAULT_VALUE) {
                const filtered = allLocations.filter((loc: any) => loc.state_id === selectedState.value);
                if (filtered.length > 0) {
                    baseFilters.location_id_in = filtered.map((loc: any) => loc.value);
                }
            }
        } else {
            baseFilters.location_id_eq = value;
        }
        onChange(baseFilters);
    }

    useEffect(() => {
        if (!fetchingLocs && locs) {
            const _states: any = {};
            const mapped = locs.map(({ location_id, location }: any) => {
                const state = location && location.state;
                if (state && !_states[state.id]) {
                    _states[state.id] = { value: state.id, label: state.name };
                }
                return { value: location_id, label: getValue(location, 'name'), state_id: state ? state.id : null };
            });
            setAllLocations(mapped);
            setLocations(mapped);
            setStates(Object.values(_states));
        }
    }, [fetchingLocs]);

    useEffect(() => {
        if (!fetchingAcs && acs) {
            setAssociateCompanies(acs.map(({ id, name }: any) => ({ value: id, label: name })));
        }
    }, [fetchingAcs]);

    return (
        <div className="d-flex flex-row filters">
            <div className="px-2">
                <label className="filter-label"><small>Company</small></label>
                <Select placeholder='Company' className="select-control"
                    options={[DEFAULT_OPTION, ...(companies || []).map(({ id, name }: any) => ({ value: id, label: name }))]}
                    onChange={handleCompanyChange}
                    value={company} isLoading={isFetching} />
            </div>
            <div className="px-2">
                <label className="filter-label"><small>Associate Company</small></label>
                <Select placeholder='Associate Company' className="select-control" isDisabled={(associateCompanies || []).length === 0}
                    options={[DEFAULT_OPTION, ...(associateCompanies || [])]}
                    onChange={handleAssociateCompanyChange} isLoading={fetchingAcs}
                    value={associate_company} />
            </div>
            <div className="px-2">
                <label className="filter-label"><small>State</small></label>
                <Select placeholder='State' className="select-control" isDisabled={states.length === 0}
                    options={[DEFAULT_OPTION, ...states]}
                    onChange={handleStateChange} isLoading={fetchingLocs}
                    value={selectedState}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }} />
            </div>
            <div className="px-2">
                <label className="filter-label"><small>Location</small></label>
                <Select placeholder='Location' className="select-control" isDisabled={(locations || []).length === 0}
                    options={[DEFAULT_OPTION, ...(locations || [])]}
                    onChange={handleLocationChange} isLoading={fetchingLocs}
                    value={location}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }} />
            </div>
        </div>
    )
}

export default OptionalLocations;
